import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Trash } from 'react-feather';
import { TagDetailsSection } from './TagDetailsSection';
import { TagDetailsSectionEditButtons } from './TagDetailsSectionEditButtons';

export type ExtractionItem = {
  name: string;
  description: string;
  ordinal: number;
};

export type TagDetailsExtractionSectionProps = {
  items: ExtractionItem[];
  /** Called with the filtered (non-empty) items when saved. */
  onSave?: (items: ExtractionItem[]) => Promise<void>;
  isSaving?: boolean;
};

function createEmpty(ordinal = 1): ExtractionItem {
  return { name: '', description: '', ordinal };
}

const DESCRIPTION =
  'Add phrases and descriptions related to the tag to improve the accuracy of transcript data extraction.';

export const TagDetailsExtractionSection = ({
  items,
  onSave,
  isSaving = false,
}: TagDetailsExtractionSectionProps) => {
  const initialEntries = items.length > 0 ? items : [createEmpty()];
  const [editing, setEditing] = useState(false);
  const [entries, setEntries] = useState<ExtractionItem[]>(initialEntries);

  const handleChange = useCallback((index: number, field: 'name' | 'description', value: string) => {
    setEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    setEntries((prev) => [...prev, createEmpty(prev.length + 1)]);
  }, []);

  const handleRemove = useCallback((index: number) => {
    setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index).map((x, i) => ({ ...x, ordinal: i + 1 }));
      return next.length > 0 ? next : [createEmpty()];
    });
  }, []);

  const handleCancel = useCallback(() => {
    setEntries(initialEntries);
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    const valid = entries
      .filter((x) => x.name.trim() !== '' && x.description.trim() !== '')
      .map((x, i) => ({ ...x, name: x.name.trim(), description: x.description.trim(), ordinal: i + 1 }));
    await onSave(valid.length > 0 ? valid : []);
    setEntries(valid.length > 0 ? valid : [createEmpty()]);
    setEditing(false);
  }, [entries, onSave]);

  const editActions = onSave ? (
    <TagDetailsSectionEditButtons
      isEditing={editing}
      isSaving={isSaving}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  ) : undefined;

  return (
    <TagDetailsSection title="Additional Context for Transcript Parsing" actions={editActions}>
      <Description>{DESCRIPTION}</Description>
      <TableHeader>
        <ColTitle>
          <ColLabel>Name</ColLabel>
        </ColTitle>
        <ColDescription>
          <ColLabel>Description</ColLabel>
        </ColDescription>
        <ColAction />
      </TableHeader>
      <Rows>
        {entries.map((entry, index) => (
          <FieldInputs
            key={entry.ordinal}
            editing={editing}
            index={index}
            name={entry.name}
            description={entry.description}
            onChange={handleChange}
            onAdd={index === entries.length - 1 ? handleAdd : undefined}
            onRemove={handleRemove}
          />
        ))}
      </Rows>
    </TagDetailsSection>
  );
};

TagDetailsExtractionSection.displayName = 'TagDetails.ExtractionSection';

/* ─── Sub-components ─── */

type FieldInputsProps = {
  editing: boolean;
  index: number;
  name: string;
  description: string;
  onChange: (index: number, field: 'name' | 'description', value: string) => void;
  onAdd?: () => void;
  onRemove?: (index: number) => void;
};

const FieldInputs = ({ editing, index, name, description, onChange, onAdd, onRemove }: FieldInputsProps) => (
  <FieldRow>
    <ColTitle>
      <FieldInput
        value={name}
        onChange={(e) => onChange(index, 'name', e.target.value)}
        readOnly={!editing}
      />
    </ColTitle>
    <ColDescription>
      <FieldInput
        value={description}
        onChange={(e) => onChange(index, 'description', e.target.value)}
        readOnly={!editing}
      />
    </ColDescription>
    <ColAction>
      {editing && onAdd && (
        <AddButton onClick={onAdd} type="button">
          + Add
        </AddButton>
      )}
      {editing && index > 0 && onRemove && (
        <RemoveButton onClick={() => onRemove(index)} type="button" title="Remove row">
          <Trash size={18} />
        </RemoveButton>
      )}
    </ColAction>
  </FieldRow>
);

/* ─── Styled ─── */

const Description = styled.div`
  font-size: 14px;
  padding: 15px 0px 10px;
`;

const TableHeader = styled.div`
  display: flex;
  gap: 16px;
  margin: 10px 0;
`;

const ColLabel = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ColTitle = styled.div`
  flex: 2;
`;
const ColDescription = styled.div`
  flex: 2.8;
`;
const ColAction = styled.div`
  flex: 0.2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--sec-01);
  border-radius: 2px;
  font-family: var(--font-reg);
  font-size: 14px;
  box-sizing: border-box;

  &:read-only {
    background-color: var(--gray-xl);
    cursor: default;
  }
`;



const AddButton = styled.button`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--pri-02);
  cursor: pointer;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  color: var(--sec-01);
  cursor: pointer;
  &:hover {
    color: var(--gray-l);
  }
`;
